import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import styled, { css, useTheme } from "styled-components";
import Link from "common/Typography/Link";
import { CreateAccountIcon, DemoAccountIcon } from "common/Icons/Icons";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography";
import { CSSProperties, useContext } from "react";
import useSharedWorkspace from "@shared/features/workspaces/useSharedWorkspace";
import { WorkspaceType } from "@shared/types";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { useBottomNavigator } from "utils/hooks/misc";

interface ChangeAccountTypeMessageProps {
  style?: CSSProperties;
}

const ChangeAccountTypeMessage = ({ style }: ChangeAccountTypeMessageProps) => {
  const theme = useTheme();
  const { resetFormValues } = useContext(AccountFormContext)!;
  const { setWorkspaceType, workspace } = useSharedWorkspace({
    allowAdminWorkspace: true,
  });
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isDemoWorkspace = workspace === WorkspaceType.DEMO;

  const onClickLink = () => {
    resetFormValues();
    const updateWorkspace = isDemoWorkspace
      ? WorkspaceType.USER
      : WorkspaceType.DEMO;
    setWorkspaceType(updateWorkspace);
  };

  const icon = isDemoWorkspace ? (
    <StyledCreateAccountIcon />
  ) : (
    <StyledDemoAccountIcon colors={theme.colors.demoTagColors.reverse()} />
  );

  const title = isDemoWorkspace
    ? "Do you want to create a live trading account?"
    : "Not sure you wish to connect an exchange account yet?";

  const linkText = isDemoWorkspace
    ? "Connect an exchange"
    : "Continue with Demo Account";

  const link = (
    <Link
      onClick={onClickLink}
      style={{ marginLeft: isDemoWorkspace || isBottomNavigator ? 0 : 4 }}
    >
      {linkText}
    </Link>
  );

  const description = isDemoWorkspace
    ? ""
    : "Create a demo account to explore Cornix features without risking real funds.";

  return (
    <MessageBg style={style}>
      {icon}
      <ContentWrapper>
        <Title level={4} $weight={typography.fontWeights.BOLD}>
          {title}
        </Title>
        <Text>
          {!isBottomNavigator && description}
          {link}
        </Text>
      </ContentWrapper>
    </MessageBg>
  );
};

export default ChangeAccountTypeMessage;

const MessageBg = styled(Flex)`
  background-color: ${(p) => p.theme.colors.lightBlue};
  justify-content: flex-start;
  padding: 16px;
  border-radius: 8px;
  text-align: start;
  width: 100%;
`;
const iconStyle = css`
  width: 37px;
  height: 32px;
`;
const StyledCreateAccountIcon = styled(CreateAccountIcon)`
  fill: ${(p) => p.theme.colors.primaryBlue};
  ${iconStyle};
`;
const StyledDemoAccountIcon = styled(DemoAccountIcon)`
  ${iconStyle};
`;
const ContentWrapper = styled.div`
  margin-left: 16px;
  flex: 1;
`;
